import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { RequireSection, SectionIndexRedirect } from '@/shared/routing';

import { CatalogStructureLayout } from './ui/CatalogStructureLayout';
import { AttributeGroupsPage } from './ui/pages/AttributeGroupsPage';
import { AttributesPage } from './ui/pages/AttributesPage';
import { CatalogLanguagesPage } from './ui/pages/CatalogLanguagesPage';
import { CategoriesPage } from './ui/pages/CategoriesPage';
import { ImportPage } from './ui/pages/ImportPage';
import { MeasurementFamiliesPage } from './ui/pages/MeasurementFamiliesPage';
import { PriceListDetailPage } from './ui/pages/PriceListDetailPage';
import { PricingPage } from './ui/pages/PricingPage';
import { ProductEditPage } from './ui/pages/ProductEditPage';
import { ProductFamiliesPage } from './ui/pages/ProductFamiliesPage';
import { ProductsPage } from './ui/pages/ProductsPage';
import { PublicationCatalogDetailPage } from './ui/pages/PublicationCatalogDetailPage';
import { PublicationPage } from './ui/pages/PublicationPage';

/**
 * Catalog feature subtree, mounted under the authenticated `AppLayout` shell.
 * `/catalog` redirects to the products workspace. The static `products/new`
 * route is ranked above the dynamic `products/:productId` editor by react-router.
 *
 * The four catalog-structure dictionaries live under one `templates` workspace
 * ({@link CatalogStructureLayout}) so the sidebar carries a single entry for the
 * whole data model instead of one line per dictionary.
 *
 * <p>Catalog is not one permission but four: browsing products and categories
 * (`catalog.view`), the PIM data model (`catalog.structure.view`), price
 * governance (`catalog.pricing.view`) and channel publication
 * (`catalog.publication.view`). Each sub-workspace is guarded on its own section
 * key, because a merchandiser who may edit products has no business reading the
 * pricing rules and the sidebar has always reflected that — only the URLs did not.
 */
export const catalogRoutes: RouteObject = {
  path: 'catalog',
  children: [
    {
      // Not a fixed hop to /catalog/products: a pricing-only or structure-only merchandiser would land
      // on a refusal for a workspace that was never theirs, with their own one path away.
      index: true,
      element: (
        <SectionIndexRedirect
        candidates={[
          'catalogProducts',
          'catalogStructure',
          'catalogPricing',
          'catalogPublication',
        ]}
      />
      ),
    },
    {
      element: <RequireSection section="catalogProducts" />,
      children: [
        { path: 'products', element: <ProductsPage /> },
        { path: 'products/new', element: <ProductEditPage /> },
        { path: 'products/:productId', element: <ProductEditPage /> },
        { path: 'categories', element: <CategoriesPage /> },
      ],
    },

    // ── Catalog data model (one sidebar entry, four tabs) ─────────────────────
    {
      path: 'templates',
      element: (
        <RequireSection section="catalogStructure">
          <CatalogStructureLayout />
        </RequireSection>
      ),
      children: [
        { index: true, element: <ProductFamiliesPage /> },
        { path: 'attribute-groups', element: <AttributeGroupsPage /> },
        { path: 'attributes', element: <AttributesPage /> },
        { path: 'units', element: <MeasurementFamiliesPage /> },
      ],
    },

    // Pre-workspace URLs. Kept as redirects rather than deleted: they are in browser histories,
    // bookmarks and older documentation, and silently landing on the products list (the `*` fallback
    // below) would look like the screen had been removed.
    { path: 'families', element: <Navigate to="/catalog/templates" replace /> },
    {
      path: 'attribute-groups',
      element: <Navigate to="/catalog/templates/attribute-groups" replace />,
    },
    { path: 'attributes', element: <Navigate to="/catalog/templates/attributes" replace /> },
    { path: 'measurements', element: <Navigate to="/catalog/templates/units" replace /> },

    {
      element: <RequireSection section="catalogStructure" />,
      children: [{ path: 'languages', element: <CatalogLanguagesPage /> }],
    },
    {
      element: <RequireSection section="catalogPricing" />,
      children: [
        { path: 'pricing', element: <PricingPage /> },
        // adr-pricing-025 (plan-platform-002 WP4.2) — the list detail is a routed sub-page, not a
        // drawer, so the STATIC-lines grid gets full width and a shareable/deep-linkable URL.
        { path: 'pricing/price-lists/:code', element: <PriceListDetailPage /> },
      ],
    },
    {
      element: <RequireSection section="catalogPublication" />,
      children: [
        { path: 'publication', element: <PublicationPage /> },
        { path: 'publication/catalogs/:catalogId', element: <PublicationCatalogDetailPage /> },
      ],
    },
    {
      element: <RequireSection section="catalogImport" />,
      children: [{ path: 'import', element: <ImportPage /> }],
    },
    {
      path: '*',
      element: (
        <SectionIndexRedirect
        candidates={[
          'catalogProducts',
          'catalogStructure',
          'catalogPricing',
          'catalogPublication',
        ]}
      />
      ),
    },
  ],
};
